// ─────────────────────────────────────────────────────────────
// Constants used across the Mapping UI.
// ─────────────────────────────────────────────────────────────

import type { TaskPhase } from './types';

/** MetaKeys reserved by ASE — surfaced with a 🔒 in the UI; renames blocked. */
const _SYSTEM_METAKEY_VALS: string[] = [
  'RoofSqFt', 'TotalDoors', 'TotalVents', 'TotalChimneys', 'TotalSkylights',
  'BaseAngleQty', 'RidgecapQty', 'SidewallSqFt', 'GableSqFt', 'TotalTrusses',
  'Area', 'Pitch', 'LengthRidge', 'LengthEave', 'LengthValley',
  'LengthRake', 'LengthHip', 'TotalRoofFacets',
  'JobTypeTuneup', 'JobTypeCleaning', 'JobTypeEnhancement',
];
/** Set for O(1) `.has()` lookups — not iterable in ES5 target; use SYSTEM_METAKEY_LIST for iteration. */
export const SYSTEM_METAKEYS: ReadonlySet<string> = new Set(_SYSTEM_METAKEY_VALS);
/** Plain array for datalists / iteration (avoids ES2015 spread requirement). */
export const SYSTEM_METAKEY_LIST: string[] = _SYSTEM_METAKEY_VALS.slice();

export const PHASE_LABELS: Record<TaskPhase, string> = {
  sales:              'Sales',
  preConstruction:    'Pre-construction',
  activeConstruction: 'Active construction',
  postConstruction:   'Post-construction',
};

/**
 * Phases whose tasks CANNOT be mapped. The Sales-phase schedule is produced before mapping runs
 * (sales scheduling is independent and happens first), so a question→task link on a Sales task is a
 * silent no-op. Sales tasks are therefore grey-shaded + non-droppable in the Tasks column and hidden
 * from the Link modal's task pickers. Single source of truth — every enforcement point reads this
 * set rather than hard-coding `'sales'`, so the policy can extend to another phase in one edit.
 */
export const NON_MAPPABLE_PHASES: ReadonlySet<TaskPhase> = new Set<TaskPhase>(['sales']);

/** True when a task's phase makes it non-mappable (see {@link NON_MAPPABLE_PHASES}). */
export const isNonMappablePhase = (phase: TaskPhase): boolean => NON_MAPPABLE_PHASES.has(phase);

/** Tooltip shown when an author hovers/drags onto a non-mappable (Sales) task. */
export const SALES_LOCK_TOOLTIP =
  "Sales phase tasks can't be mapped — sales scheduling runs before mapping.";

/** Phase groups that start collapsed on each load of the Mapping tab (in-memory only). Array (not a
 *  Set) so it can seed component state under the ES5 target without Set iteration. */
export const DEFAULT_COLLAPSED_PHASES: readonly TaskPhase[] = ['sales'];

// Units a task can actually carry — must match the Task editor's Unit dropdown
// (tenant-web src/utils/TaskUtils.ts ALLOWED_UNITS = ["sq.ft", "ft", "EA"]). The populate-link
// Unit field is constrained to this set so an author can't pick a unit the task editor rejects.
export const INSTALL_TASK_UNITS: string[] = ['sq.ft', 'ft', 'EA'];

export const TASK_UNITS: { value: string; label: string }[] = [
  { value: '',      label: '—' },
  { value: 'EA',    label: 'EA · each' },
  { value: 'ft',    label: 'ft · feet' },
  { value: 'in',    label: 'in · inches' },
  { value: 'sq.ft', label: 'sq.ft · square feet' },
  { value: 'cu.ft', label: 'cu.ft · cubic feet' },
  { value: 'LF',    label: 'LF · linear feet' },
  { value: 'SF',    label: 'SF · square feet' },
  { value: 'cu.yd', label: 'cu.yd · cubic yards' },
  { value: 'lb',    label: 'lb · pounds' },
  { value: 'gal',   label: 'gal · gallons' },
  { value: 'hr',    label: 'hr · hours' },
  { value: '%',     label: '% · percent' },
];

/** Math operator buttons shown above the formula input. */
export const FORMULA_OPERATORS: { label: string; insert: string; title: string }[] = [
  { label: '+',   insert: ' + ',    title: 'Add' },
  { label: '−',   insert: ' - ',    title: 'Subtract' },
  { label: '×',   insert: ' * ',    title: 'Multiply' },
  { label: '÷',   insert: ' / ',    title: 'Divide' },
  { label: '( )', insert: '()',     title: 'Parentheses' },
  { label: '%',   insert: ' / 100', title: 'Percent (divide by 100)' },
  { label: 'x²',  insert: '**2',    title: 'Square' },
  { label: '√',   insert: 'sqrt()', title: 'Square root' },
  { label: '⌊⌋',  insert: 'round()',title: 'Round' },
  { label: 'abs', insert: 'abs()',  title: 'Absolute value' },
  { label: 'min', insert: 'min(,)', title: 'Minimum of two values' },
  { label: 'max', insert: 'max(,)', title: 'Maximum of two values' },
];

/** JS identifiers we should NOT treat as formula variables. */
export const RESERVED_FORMULA_TOKENS: ReadonlySet<string> = new Set([
  'Math', 'sqrt', 'pow', 'round', 'floor', 'ceil', 'abs', 'PI',
  'parseInt', 'parseFloat', 'min', 'max',
]);

/** A modest Roof Enhancement task template for demoing the mapping pane.
 *  Replace with your real per-trade task template at integration time. */
export const ROOF_ENHANCEMENT_TASKS = [
  { id: 1,  wbs: '1',    phase: 'preConstruction',    taskName: 'Pre-Construction',                  dependencies: '',   estimatedHours: 0.25, taskType: 'Manual Task',     additionalData: '' as const },
  { id: 2,  wbs: '1.1',  phase: 'preConstruction',    taskName: 'Site visit & measurements',         dependencies: '1',  estimatedHours: 2,    taskType: 'Manual Task',     additionalData: '' as const },
  { id: 3,  wbs: '1.2',  phase: 'preConstruction',    taskName: 'Generate PO for shingles',          dependencies: '2',  estimatedHours: 0.25, taskType: 'Create PO Task',  additionalData: '' as const },
  { id: 4,  wbs: '1.3',  phase: 'preConstruction',    taskName: 'Approve & send PO for shingles',    dependencies: '3',  estimatedHours: 0.08, taskType: 'Approve PO Task', additionalData: '' as const },
  { id: 5,  wbs: '1.4',  phase: 'preConstruction',    taskName: 'Generate PO for vent boots',        dependencies: '2',  estimatedHours: 0.25, taskType: 'Create PO Task',  additionalData: '' as const },
  { id: 6,  wbs: '1.5',  phase: 'preConstruction',    taskName: 'Generate PO for chimney flashing',  dependencies: '2',  estimatedHours: 0.25, taskType: 'Create PO Task',  additionalData: '' as const },
  { id: 10, wbs: '2.1',  phase: 'activeConstruction', taskName: 'Tear off existing roof',            dependencies: '4',  estimatedHours: 6,    taskType: 'Manual Task',     additionalData: '' as const },
  { id: 11, wbs: '2.2',  phase: 'activeConstruction', taskName: 'Install underlayment',              dependencies: '10', estimatedHours: 3,    taskType: 'Install Task',    additionalData: '' as const },
  { id: 12, wbs: '2.3',  phase: 'activeConstruction', taskName: 'Flash chimneys',                    dependencies: '11', estimatedHours: 1.5,  taskType: 'Install Task',    additionalData: '' as const },
  { id: 13, wbs: '2.4',  phase: 'activeConstruction', taskName: 'Flash skylights',                   dependencies: '11', estimatedHours: 1,    taskType: 'Install Task',    additionalData: '' as const },
  { id: 14, wbs: '2.5',  phase: 'activeConstruction', taskName: 'Install vent boots',                dependencies: '11', estimatedHours: 0.75, taskType: 'Install Task',    additionalData: '' as const },
  { id: 15, wbs: '2.6',  phase: 'activeConstruction', taskName: 'Install shingles',                  dependencies: '14', estimatedHours: 8,    taskType: 'Install Task',    additionalData: '' as const },
  { id: 16, wbs: '2.7',  phase: 'activeConstruction', taskName: 'Install ridge vent',                dependencies: '15', estimatedHours: 1,    taskType: 'Install Task',    additionalData: '' as const },
  { id: 17, wbs: '2.8',  phase: 'activeConstruction', taskName: 'Steep-slope safety setup',          dependencies: '10', estimatedHours: 0.5,  taskType: 'Manual Task',     additionalData: '' as const },
  { id: 18, wbs: '2.9',  phase: 'activeConstruction', taskName: 'Install ice & water shield',        dependencies: '11', estimatedHours: 1.5,  taskType: 'Install Task',    additionalData: '' as const },
  { id: 19, wbs: '2.10', phase: 'activeConstruction', taskName: 'Install premium warranty kit',      dependencies: '15', estimatedHours: 0.5,  taskType: 'Install Task',    additionalData: '' as const },
  { id: 30, wbs: '3.1',  phase: 'postConstruction',   taskName: 'Cleanup & magnetic sweep',          dependencies: '16', estimatedHours: 1,    taskType: 'Manual Task',     additionalData: '' as const },
  { id: 31, wbs: '3.2',  phase: 'postConstruction',   taskName: 'Final inspection',                  dependencies: '30', estimatedHours: 0.5,  taskType: 'Manual Task',     additionalData: '' as const },
] as const;
